import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { getFeedbackForm } from "../../../../redux/reducer/incbotFeedbackReducer";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import {
  ChevronDown,
  ChevronRight,
  Play,
  Pause,
  Volume2,
  VolumeX,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { Badge } from "../../../ui/badge";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";

interface ScreeningQuestionsViewProps {
  isPrint?: boolean;
}

const ScreeningQuestionsView: React.FC<ScreeningQuestionsViewProps> = ({ isPrint = false }) => {
  const feedbackForm = useSelector(getFeedbackForm);
  const sections = feedbackForm?.cefrScreeningSections ?? [];
  const hasSections = sections.length > 0;

  if (!hasSections) {
    return (
      <div className="p-3 md:p-6">
        <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
          Screening Questions
        </div>
        <div className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
          <p className="text-gray-600 text-sm md:text-base">
            No screening questions for this CEFR assessment.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Screening Questions
      </div>

      <div className="space-y-3">
        {sections.map((section: any, sectionIndex: number) => (
          <CefrSectionCard
            key={section.id || sectionIndex}
            section={section}
            defaultExpanded={isPrint || (section.defaultExpanded ?? sectionIndex === 0)}
          />
        ))}
      </div>
    </div>
  );
};

interface CefrSectionCardProps {
  section: any;
  defaultExpanded?: boolean;
}

const CefrSectionCard: React.FC<CefrSectionCardProps> = ({ section, defaultExpanded = false }) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);

  const hasReadingItems = (section.readingItems?.length ?? 0) > 0;
  const hasWritingItems = (section.writingItems?.length ?? 0) > 0;
  const hasSpeakingItems = (section.speakingItems?.length ?? 0) > 0;

  const hasContent =
    (section.audioItems?.length ?? 0) > 0 ||
    (section.questions?.length ?? 0) > 0 ||
    !!section.paragraph ||
    hasReadingItems ||
    !!(section.writingPrompt ?? section.userInput) ||
    hasWritingItems ||
    !!(section.speakingTopic ?? section.recordingUrl) ||
    hasSpeakingItems;

  const hasParagraph = !!section.paragraph;
  const hasAudioItems = (section.audioItems?.length ?? 0) > 0;
  const hasWritingContent = !!(section.writingPrompt ?? section.userInput) || hasWritingItems;
  const hasSpeakingContent = !!(section.speakingTopic ?? section.recordingUrl) || hasSpeakingItems;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-transparent rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="bg-gradient-to-r from-blue-50 to-indigo-50 w-full flex items-center justify-between p-4 md:p-5 hover:bg-gray-50/50 transition-colors text-left"
          >
            <div className="flex-1 min-w-0 pr-4">
              <h3 className="text-base md:text-lg font-semibold text-gray-900">{section.title}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{section.description}</p>
            </div>
            <div className="flex-shrink-0 text-gray-400">
              {isOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-gray-100 px-4 md:px-5 py-4 md:py-5 bg-gray-50/30">
            {/* Reading Section: one passage + questions per item */}
            {hasReadingItems &&
              section.readingItems?.map((readingItem: any, rIndex: number) => (
                <div key={readingItem.id || rIndex} className={rIndex > 0 ? "mt-6" : ""}>
                  {readingItem.paragraph && (
                    <div className="mb-4">
                      <ParagraphCard paragraph={readingItem.paragraph} />
                    </div>
                  )}
                  <div className="space-y-4">
                    {readingItem.questions?.map((q: any, qIndex: number) => (
                      <QuestionCard key={qIndex} question={q} />
                    ))}
                  </div>
                </div>
              ))}
            {/* Reading Section: legacy paragraph fallback */}
            {hasParagraph && (
              <div className="mb-6">
                <ParagraphCard paragraph={section.paragraph} />
                {section.questions?.length > 0 && (
                  <div className="mt-4 space-y-4">
                    {section.questions.map((q: any, qIndex: number) => (
                      <QuestionCard key={qIndex} question={q} />
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Listening Section: Audio items */}
            {hasAudioItems &&
              section.audioItems?.map((audioItem: any, audioIndex: number) => (
                <div key={audioItem.id || audioIndex} className={audioIndex > 0 ? "mt-6" : ""}>
                  <div className="mb-4">
                    <span className="text-sm font-medium text-gray-700">{audioItem.label}</span>
                    <AudioPlayer audioUrl={audioItem.audioUrl} />
                  </div>
                  <div className="space-y-4">
                    {audioItem.questions?.map((q: any, qIndex: number) => (
                      <QuestionCard key={qIndex} question={q} />
                    ))}
                  </div>
                </div>
              ))}
            {/* Writing Section: Prompt + User Input (single or multiple) */}
            {hasWritingContent && (
              <>
                {hasWritingItems ? (
                  section.writingItems?.map((item: any, idx: number) => (
                    <div key={idx} className={idx > 0 ? "mt-6" : ""}>
                      <WritingSectionCard writingPrompt={item.prompt} userInput={item.userInput} />
                    </div>
                  ))
                ) : (
                  <WritingSectionCard
                    writingPrompt={section.writingPrompt}
                    userInput={section.userInput}
                  />
                )}
              </>
            )}
            {/* Speaking Section: Topic + Recording (single or multiple) */}
            {hasSpeakingContent && (
              <>
                {hasSpeakingItems ? (
                  section.speakingItems?.map((item: any, idx: number) => (
                    <div key={idx} className={idx > 0 ? "mt-6" : ""}>
                      <SpeakingSectionCard
                        speakingTopic={item.topic}
                        recordingUrl={item.recordingUrl}
                      />
                    </div>
                  ))
                ) : (
                  <SpeakingSectionCard
                    speakingTopic={section.speakingTopic}
                    recordingUrl={section.recordingUrl}
                  />
                )}
              </>
            )}
            {/* Standalone questions (no paragraph, no audio, no writing, no speaking) */}
            {!hasParagraph &&
              !hasReadingItems &&
              !hasAudioItems &&
              !hasWritingContent &&
              !hasSpeakingContent &&
              section.questions?.map((q: any, qIndex: number) => (
                <QuestionCard key={qIndex} question={q} />
              ))}
            {!hasContent && <p className="text-sm text-gray-500">No questions in this section.</p>}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

interface WritingSectionCardProps {
  writingPrompt?: string;
  userInput?: string;
}

const WritingSectionCard: React.FC<WritingSectionCardProps> = ({ writingPrompt, userInput }) => (
  <div className="bg-white p-4 md:p-5 rounded-lg border border-gray-200">
    {writingPrompt && (
      <div className="mb-6">
        <span className="text-sm font-semibold text-gray-700 block mb-2">Writing Prompt</span>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-gray-200 border-l-4 border-l-[#133F7D] p-4 text-sm md:text-base text-gray-800 leading-relaxed">
          {writingPrompt}
        </div>
      </div>
    )}
    {userInput !== undefined && userInput !== null && (
      <div>
        <span className="text-sm font-semibold text-gray-700 block mb-2">User Input</span>
        <div className="bg-[#F3F4F6] rounded-lg border border-gray-200 p-4 text-sm md:text-base text-gray-800 leading-relaxed">
          {userInput}
        </div>
      </div>
    )}
  </div>
);

interface SpeakingSectionCardProps {
  speakingTopic?: string;
  recordingUrl?: string;
}

const SpeakingSectionCard: React.FC<SpeakingSectionCardProps> = ({
  speakingTopic,
  recordingUrl,
}) => (
  <div className="bg-white p-4 md:p-5 rounded-lg border border-gray-200 shadow-sm">
    {speakingTopic && (
      <div className="mb-6">
        <span className="text-sm font-semibold text-gray-700 block mb-2">Speaking Topic</span>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-gray-200 border-l-4 border-l-[#133F7D] p-4 text-sm md:text-base text-gray-800 leading-relaxed">
          {speakingTopic}
        </div>
      </div>
    )}
    <div>
      <span className="text-sm font-semibold text-gray-700 block mb-2">Recording</span>
      <div className="rounded-lg overflow-hidden border border-gray-200 bg-black">
        <video
          src={
            recordingUrl ||
            "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4"
          }
          controls
          className="w-full aspect-video"
          preload="metadata"
        >
          Your browser does not support the video tag.
        </video>
      </div>
    </div>
  </div>
);

interface ParagraphCardProps {
  paragraph: {
    title?: string;
    intro?: string;
    content?: string;
    paragraphs?: string[];
  };
}

const ParagraphCard: React.FC<ParagraphCardProps> = ({ paragraph }) => {
  const bodyParagraphs =
    paragraph.paragraphs ??
    (paragraph.content ? paragraph.content.split(/\n\n+/).filter(Boolean) : []);

  return (
    <div className="bg-white p-4 md:p-5 rounded-lg border border-gray-200">
      <span className="text-sm font-semibold text-gray-700 block mb-3">Paragraph</span>
      {paragraph.title && (
        <h4 className="text-xl font-bold text-gray-900 mb-2">{paragraph.title}</h4>
      )}
      {/* {paragraph.intro && (
        <p className="text-base text-gray-700 mb-4">{paragraph.intro}</p>
      )} */}
      <div className="space-y-4">
        {bodyParagraphs.map((text: string, i: number) => (
          <p key={i} className="text-sm md:text-base text-gray-800 leading-relaxed">
            {text}
          </p>
        ))}
      </div>
    </div>
  );
};

interface AudioPlayerProps {
  audioUrl?: string;
}

const formatTime = (seconds: number): string => {
  if (!isFinite(seconds) || isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDuration(audio.duration);
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const val = Number(e.target.value);
    audio.currentTime = val;
    setCurrentTime(val);
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !muted;
    setMuted(!muted);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-200 mt-2">
      {audioUrl && <audio ref={audioRef} src={audioUrl} preload="metadata" />}
      <button
        type="button"
        onClick={togglePlay}
        disabled={!audioUrl}
        className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-900 text-white hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4" fill="currentColor" />
        ) : (
          <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
        )}
      </button>
      <div className="flex-1 min-w-0">
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.1}
          value={currentTime}
          onChange={handleSeek}
          disabled={!audioUrl || duration === 0}
          className="w-full h-2 rounded-full appearance-none cursor-pointer accent-gray-900 bg-gray-200"
          style={{
            background: `linear-gradient(to right, #020610 ${progress}%, #e5e7eb ${progress}%)`,
          }}
        />
        <span className="text-xs text-gray-500  block">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
      <button
        type="button"
        onClick={toggleMute}
        className="flex-shrink-0 p-2 text-gray-500 hover:text-gray-700"
      >
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
    </div>
  );
};

interface QuestionCardProps {
  question: {
    number: string;
    text: string;
    options?: string[];
    userAnswer: string | number;
    correctAnswer: string;
    isCorrect: boolean;
  };
}

const QuestionCard: React.FC<QuestionCardProps> = ({ question }) => {
  const userAnswerText =
    question.options &&
    question.userAnswer !== undefined &&
    question.userAnswer !== null &&
    question.userAnswer !== ""
      ? (question.options[Number(question.userAnswer)] ?? question.userAnswer)
      : question.userAnswer === ""
        ? "Candidate skipped the question"
        : question.userAnswer;
  return (
    <div className="bg-white p-4 md:p-5 rounded-lg border border-gray-200">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-md font-semibold text-gray-700 flex-shrink-0">
              {question.number}.
            </span>
            <p className="text-sm md:text-base text-gray-900 leading-relaxed">{question.text}</p>
          </div>
          {(question.options?.length ?? 0) > 0 && (
            <div className="space-y-1.5 mb-4 text-sm text-gray-700">
              {question.options!.map((opt: string, i: number) => (
                <div key={i}>{opt}</div>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">User Input:</Label>
              <Input
                value={userAnswerText || "No answer provided"}
                readOnly
                className={
                  question.isCorrect
                    ? "bg-green-50 border-green-200 text-green-900 text-sm"
                    : "bg-red-50 border-red-200 text-red-900 text-sm"
                }
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">
                Correct Answer:
              </Label>
              <Input
                value={question.correctAnswer}
                readOnly
                className="bg-blue-50 border-blue-200 text-blue-900 text-sm"
              />
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-1.5">
          {question.isCorrect ? (
            <>
              <CheckCircle className="text-green-500" size={18} />
              <Badge
                variant="outline"
                className="text-green-700 border-green-200 bg-green-50 text-xs lg:text-sm"
              >
                Correct
              </Badge>
            </>
          ) : (
            <>
              <XCircle className="text-red-500" size={18} />
              <Badge
                variant="outline"
                className="text-red-700 border-red-200 bg-red-50 text-xs lg:text-sm"
              >
                Incorrect
              </Badge>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScreeningQuestionsView;
